import React from 'react'
import styled, { css } from 'styled-components'
import PropTypes from 'prop-types'
import { media } from 'src/styles/styled-components'
import ArBaseContainer from 'src/components/ArBaseContainer/ArBaseContainer'
import ArBaseImage from 'src/components/ArBaseImage/ArBaseImage'

const $vertGap = ['40px', '60px', '95px'] // [mobile, tablet, desktop]

const StyledWrapper = styled.section`
  background-color: ${ p => p.theme.colors.black };
  color: ${ p => p.theme.colors.white };
  font-weight: ${ ({ theme }) => theme.fontWeight.primary.Light };
  padding-bottom: 40px;
  padding-top: 30px;

  ${ media.tablet`
    padding-top: 60px;
    padding-bottom: 60px;
  ` }

  ${ media.desktop`
    padding-top: 100px;
    padding-bottom: 170px;
  ` }
`

const StyledContainer = styled(ArBaseContainer)`
  margin-bottom: ${ '-' + $vertGap[0] };
  ${ p =>
    p.scCompact &&
    css`
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
    ` }
  ${ media.tablet`
    margin-bottom: ${ '-' + $vertGap[1] };
  ` }

  ${ media.desktop`
    margin-bottom: ${ '-' + $vertGap[1] };
  ` }
`

const StyledTitle = styled.h2`
  font-size: 2.8rem;
  line-height: 1.18;
  margin-bottom: 20px;
  text-align: center;

  ${ media.tablet`
    font-size: 3.8rem;
    margin-bottom: 10px;
  ` }

  ${ media.desktop`
    line-height: 1.2;
    font-size: 4.5rem;
    margin-bottom: 20px;
  ` }
`

const StyledDescription = styled.p`
  font-size: 1.8rem;
  line-height: 1.6;
  text-align: center;

  ${ media.tablet`
    letter-spacing: 0.01em;
  ` }

  ${ media.desktop`
    font-size: 2.8rem;
    letter-spacing: normal;
  ` }
`

const StyledList = styled.ul`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-top: 40px;
  ${ media.tablet`
    margin-top: 60px;

    ${ p =>
    p.scCompact &&
      css`
        justify-content: center;
        margin-left: -12px;
        margin-right: -12px;
      ` }
  ` }

  ${ media.desktop`
    margin-top: 105px;
  ` }
`

const StyledItem = styled.li`
  align-items: flex-start;
  display: flex;
  flex-direction: row;
  margin-bottom: ${ $vertGap[0] };
  width: 100%;

  ${ p =>
    p.scCompact &&
    css`
      align-items: center;
      flex-direction: column;
      width: 45%;
    ` }
  ${ media.tablet`
    align-items: center;
    flex-direction: column;
    margin-bottom: ${ $vertGap[1] };
    width: 30%;
    ${ p =>
    p.scCompact &&
      css`
        padding-left: 12px;
        padding-right: 12px;
        width: 33.33%;
      ` }
  ` }

  ${ media.desktop`
    margin-bottom: ${ $vertGap[2] };
    ${ p =>
    p.scCompact &&
      css`
        margin-left: 0;
        margin-right: 0;
        width: 23%;
      ` }
  ` }
`

const StyledItemInner = styled.div`
  width: 100%;
`

const StyledItemImage = styled(ArBaseImage)`
  width: 100px;
  height: auto;
  flex-shrink: 0;
  margin-top: 5px;
  margin-bottom: 20px;
  ${ p =>
    !p.scCompact &&
    css`
      margin-bottom: 0;
      margin-right: 20px;
    ` }
  ${ media.tablet`
    margin-bottom: 20px
    margin-right: 0;
  ` }
`

const StyledItemTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: ${ ({ theme }) => theme.fontWeight.primary.Bold };
  letter-spacing: 0.01em;
  line-height: 1.55;
  margin-bottom: 10px;
  ${ p =>
    p.scCompact &&
    css`
      text-align: center;
    ` }
  ${ media.tablet`
    text-align: center;
    margin-bottom: 15px;
  ` }

  ${ media.desktop`
    margin-bottom: 10px;
    line-height: 1.6;
    font-size: 2.8rem;
    font-weight: ${ ({ theme }) => theme.fontWeight.primary.Light };
  ` }
`

const StyledItemDescription = styled.p`
  font-size: 1.4rem;
  letter-spacing: 0.01em;
  line-height: 1.57;
  ${ p =>
    p.scCompact &&
    css`
      text-align: center;
    ` }
  ${ media.tablet`
    line-height: 1.55;
    font-size: 1.8rem;
    text-align: center;
    letter-spacing: 0.01em;
  ` }
`

class ArBenefits extends React.Component {
  static propTypes = {
    title: PropTypes.string,
    description: PropTypes.string,
    benefits: PropTypes.arrayOf(
      PropTypes.shape({
        image: PropTypes.object,
        title: PropTypes.string,
        description: PropTypes.string,
      })
    ),
  };

  render () {
    const { title, description, benefits, scCompact } = this.props
    return (
      <StyledWrapper>
        <StyledContainer>
          <StyledTitle>{title}</StyledTitle>
          <StyledDescription>{description}</StyledDescription>
          <StyledList scCompact={scCompact}>
            {benefits.map(({ image, title, description }, index) => (
              <StyledItem key={index} scCompact={scCompact}>
                <StyledItemImage
                  scCompact={scCompact}
                  src={image.url}
                  alt={image.alt}
                  scAspectRatio={1}
                />
                <StyledItemInner>
                  <StyledItemTitle scCompact={scCompact}>
                    {title}
                  </StyledItemTitle>
                  <StyledItemDescription scCompact={scCompact}>
                    {description}
                  </StyledItemDescription>
                </StyledItemInner>
              </StyledItem>
            ))}
          </StyledList>
        </StyledContainer>
      </StyledWrapper>
    )
  }
}

export default ArBenefits
